import { useState, useCallback } from 'react'

/**
 * Hook for password validation with consistent rules across the app
 * Validates:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */
export function usePasswordValidation() {
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  })

  /**
   * Validates password strength and returns an object with boolean checks
   * @param {string} password - The password to validate
   * @returns {Object} Object with validation results
   */
  const validatePasswordStrength = useCallback((password) => {
    return {
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(password)
    }
  }, [])

  /**
   * Updates password strength state based on password value
   * @param {string} password - The password to validate
   */
  const updatePasswordStrength = useCallback((password) => {
    const strength = validatePasswordStrength(password)
    setPasswordStrength(strength)
  }, [validatePasswordStrength])

  /**
   * Validates if password meets all requirements
   * @param {string} password - The password to validate
   * @returns {Object} { isValid: boolean, errors: string[] }
   */
  const validatePassword = useCallback((password) => {
    const strength = validatePasswordStrength(password)
    const errors = []

    if (!strength.hasMinLength) {
      errors.push('Das Passwort muss mindestens 8 Zeichen lang sein.')
    }
    if (!strength.hasUpperCase) {
      errors.push('Das Passwort muss mindestens einen Großbuchstaben enthalten.')
    }
    if (!strength.hasLowerCase) {
      errors.push('Das Passwort muss mindestens einen Kleinbuchstaben enthalten.')
    }
    if (!strength.hasNumber) {
      errors.push('Das Passwort muss mindestens eine Zahl enthalten.')
    }
    if (!strength.hasSpecialChar) {
      errors.push('Das Passwort muss mindestens ein Sonderzeichen enthalten (!@#$%^&* etc.).')
    }

    return {
      isValid: errors.length === 0,
      errors,
      strength
    }
  }, [validatePasswordStrength])

  /**
   * Checks if password and confirmPassword match
   * @param {string} password - The password
   * @param {string} confirmPassword - The confirmation password
   * @returns {boolean}
   */
  const passwordsMatch = useCallback((password, confirmPassword) => {
    return password === confirmPassword
  }, [])

  return {
    passwordStrength,
    validatePasswordStrength,
    updatePasswordStrength,
    validatePassword,
    passwordsMatch
  }
}

